import { createPortal } from 'react-dom'
import { forwardRef, useState } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import ModalBase, * as Modal from '@segment/matcha/components/Modal'
import Image from '@segment/matcha/components/Image'
import VideoPlayerBase, * as VideoPlayer from '@segment/matcha/components/VideoPlayer'

import usePortal from '@segment/utils/usePortal'

interface VideoModalProps {
  url: string
  cover: string
  autoplay?: boolean
}

const VideoModal = forwardRef<HTMLDivElement, VideoModalProps>(({ url, cover, autoplay }, ref) => {
  const theme = useTheme()
  const modalsRoot = usePortal(`video-modal`)
  const [playing, setPlaying] = useState(false)
  const play = () => setPlaying(true)
  const stop = () => setPlaying(false)

  return createPortal(
    <AppearanceProvider appearance="twilioLight">
      <StyledModal size="xlarge" ref={ref} placement="center">
        <Modal.Body>
          <VideoPlayerBase
            url={url}
            playing={autoplay ? true : playing}
            onPause={stop}
            onPlay={play}
            config={{
              wistia: {
                options: { playerColor: theme.palette.twilio.gray[100], playButton: false, smallPlayButton: false },
              },
            }}
          >
            {!playing && <VideoPlayer.BigPlayButton ariaLabel="Play Button" onClick={play} />}
            {!playing && (
              <VideoPlayer.Cover key="cover">
                <Picture>
                  <source type="image/webp" srcSet={cover} />
                  <StyledImage width="800" height="455" src={cover} alt="Illustration: Cover" loading="lazy" />
                </Picture>
              </VideoPlayer.Cover>
            )}
          </VideoPlayerBase>
        </Modal.Body>
      </StyledModal>
    </AppearanceProvider>,
    modalsRoot
  )
})

const StyledModal = styled(ModalBase)`
  max-width: 800px;
`

const Picture = styled.picture`
  margin: 0 auto;
`

const StyledImage = styled(Image)`
  background-color: ${({ theme }) => theme.palette.twilio.gray[100]};
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export default VideoModal
